import { Communicator } from '../tools/Communication';
import { downloadAsPdf } from './download';

export interface LabelData {
  yourInternalID: number;
  parcelNo: string;
}

export interface LabelResponse {
  labelPdf: string;
  labelDataList: LabelData[];
}
export interface ErrorDataType {
  errorId: number;
  errorCode: string;
  errorMsgShort: string;
  errorMsgLong: string;
  internalId?: string;
}

export interface SetOrderResponse {
  labelResponse: LabelResponse;
  version: number;
  ack: boolean;
  timeStamp: string;
  errorDataList: ErrorDataType[];
}

export interface CODType {
  purpose: string;
  amount: number;
  payment: string;
}

export interface ParcelDataType {
  shipService: string;
  weight: number;
  content: string;
  yourInternalID: number;
  reference1: string;
  reference2?: string;
  cOD?: CODType;
}

export interface AddressType {
  company: string;
  salutation: string;
  name: string;
  street: string;
  houseNo: string;
  country: string;
  zipCode: string;
  city: string;
  state: string;
  phone: string;
  mail: string;
  gender?: string;
  firstName?: string;
  lastName?: string;
}

export interface OrderDataType {
  shipAddress: AddressType;
  parcelShopID: number;
  parcelData: ParcelDataType;
}

export interface ErrorWithOrderId {
  orderId: string;
  errorId: number;
  errorCode: string;
  errorMsgShort: string;
  errorMsgLong: string;
}

export interface CreateShippingLabelsResult {
  success: boolean;
  failedOrderIds: string[];
  successfulOrderIds: string[];
  errorCount: number;
  hasSuccessfulLabels: boolean;
  labelPdf?: string;
  errors?: ErrorWithOrderId[];
  successfulCount?: number;
  failedCount?: number;
}

export async function createShippingLabels(
  orderData: OrderDataType[],
  vytalIdByInternalDPDId: { [key: number]: string },
  filename = 'shipping_labels',
  downloadPdf = false,
): Promise<CreateShippingLabelsResult> {
  const request = {
    orderData,
    vytalIdByInternalDPDId,
  };
  const response = await Communicator.PostData(
    'ShippingLabel/CreateOrders',
    request,
  );
  const data: {
    mergedShippingLabelPdf?: string;
    successfulOrderIds?: string[];
    failedOrderIds?: string[];
    errors?: ErrorWithOrderId[];
    successfulCount?: number;
    failedCount?: number;
    version?: number;
    ack?: boolean;
    timeStamp?: string;
  } = await response.json();

  const failedOrderIds = data.failedOrderIds || [];
  const successfulOrderIds = data.successfulOrderIds || [];
  const errors = data.errors || [];
  const labelPdf = data.mergedShippingLabelPdf;

  // Download PDF only if requested (for backward compatibility)
  if (downloadPdf && labelPdf) {
    downloadAsPdf(labelPdf, `${filename}.pdf`);
  }

  return {
    success: failedOrderIds.length === 0,
    failedOrderIds: failedOrderIds,
    successfulOrderIds: successfulOrderIds,
    errorCount: errors.length,
    hasSuccessfulLabels: successfulOrderIds.length > 0,
    labelPdf: labelPdf,
    errors: errors,
    successfulCount: data.successfulCount || successfulOrderIds.length,
    failedCount: data.failedCount || failedOrderIds.length,
  };
}

export function extendErrorWithInternalId(
  errorIn: ErrorDataType,
  vytalIdByInternalDPDId: { [key: number]: string },
) {
  const error = { ...errorIn };
  if (error.errorMsgLong.startsWith('YourInternalID')) {
    const end = error.errorMsgLong.indexOf(':');
    const internalId = parseInt(error.errorMsgLong.substring(15, end));
    console.log(internalId);
    console.log(Object.keys(vytalIdByInternalDPDId));
    error.internalId = vytalIdByInternalDPDId[internalId];
    error.errorMsgLong = error.errorMsgLong.substring(end + 2);
  }
  return error;
}

export async function checkShippingLabels(
  orderData: OrderDataType[],
  orderExecutionDate: Date,
) {
  if (Date.now() > orderExecutionDate.getTime()) {
    return [];
  }
  const response = await Communicator.PostData('ShippingLabel/TestOrders', {
    orderData,
    orderExecutionDate,
  });
  const data: SetOrderResponse = await response.json();
  return data.errorDataList || [];
}
